import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { of, catchError, switchMap, EMPTY, map } from 'rxjs';

import {
  BlockService,
  Product,
  ProductionService,
  ProductService,
  ServerResponseService,
} from '@speedy/core';

@Injectable()
export class ProductResolver {
  private readonly productService = inject(ProductService);
  private readonly serverResponseService = inject(ServerResponseService);
  private readonly productionService = inject(ProductionService);
  private readonly blockService = inject(BlockService);

  resolve(route: ActivatedRouteSnapshot) {
    const urlKey = route.params['url_key'];
    const product = this.productionService.getDataFromTransferState('product') as
      | Product
      | undefined;

    if (product?.url_key === urlKey) {
      return of(product as Product);
    }

    return this.productService.getProductByUrlKey(urlKey).pipe(
      switchMap((product) => {
        this.productionService.setDataToTransferState('product', product);
        return this.preloadBlocks(product);
      }),
      catchError(() => {
        this.serverResponseService.setNotFound();
        return EMPTY;
      }),
    );
  }

  /**
   * Preloads blocks based on the product's features.
   * @param product - The product object containing the `product_features` property.
   */
  preloadBlocks(product: Product) {
    if (product?.product_features && product?.product_features.length) {
      return this.blockService
        .requestBlocksByIdentifier(product.product_features.split(','))
        .pipe(map(() => product));
    }

    return of(product);
  }
}
